import React, { useMemo } from "react";
import { GeoJson, Marker, Overlay } from "pigeon-maps";
import type { EuropeHighlight, HighlightSection } from "../../types/europe-highlights";
import { sanitizeGeoJsonForMercator } from "../../utils/sanitize-geojson-mercator";

type Props = {
  highlight: EuropeHighlight | null | undefined;
  /** Optional section to emphasize (others dim). */
  activeSectionId?: string | null;
  zoom: number;
  // Injected by pigeon-maps when this component is a Map child
  mapState?: unknown;
  latLngToPixel?: unknown;
  pixelToLatLng?: unknown;
};

function sectionLine(section: HighlightSection) {
  return {
    type: "Feature" as const,
    properties: { id: section.id, kind: "section" },
    geometry: {
      type: "LineString" as const,
      coordinates: [
        [section.start.lng, section.start.lat],
        [section.end.lng, section.end.lat],
      ],
    },
  };
}

/**
 * Draws river corridor + put-in / take-out markers for a rafting (etc.) highlight.
 * Must be a direct child of pigeon-maps <Map> so map props are injected.
 */
const HighlightRiverOverlay: React.FC<Props> = ({
  highlight,
  activeSectionId = null,
  zoom,
  mapState,
  latLngToPixel,
  pixelToLatLng,
}) => {
  const mapProps = { mapState, latLngToPixel, pixelToLatLng };

  const riverData = useMemo(() => {
    if (!highlight?.riverLine?.length) return null;
    return sanitizeGeoJsonForMercator({
      type: "FeatureCollection" as const,
      features: [
        {
          type: "Feature" as const,
          properties: { kind: "river", slug: highlight.slug },
          geometry: {
            type: "LineString" as const,
            coordinates: highlight.riverLine,
          },
        },
      ],
    });
  }, [highlight]);

  const sectionData = useMemo(() => {
    const sections = highlight?.sections || [];
    if (!sections.length) return null;
    return sanitizeGeoJsonForMercator({
      type: "FeatureCollection" as const,
      features: sections.map(sectionLine),
    });
  }, [highlight]);

  if (!highlight || !mapState || !latLngToPixel) return null;
  // Avoid projecting while the map has no size (pigeon can emit Inf paths)
  const ms = mapState as { width?: number; height?: number };
  if (!ms.width || !ms.height) return null;
  const sections = highlight.sections || [];
  if (!riverData && !sections.length) return null;

  const strokeW = zoom >= 11 ? 5 : zoom >= 9 ? 4 : 3;
  const showLabels = zoom >= 10;

  return (
    <>
      {riverData && (
        <GeoJson
          {...mapProps}
          data={riverData}
          hover={false}
          styleCallback={() => ({
            strokeWidth: String(strokeW),
            stroke: "#1E90FF",
            fill: "none",
            strokeLinecap: "round",
            strokeLinejoin: "round",
            opacity: "0.95",
            r: "0",
          })}
        />
      )}
      {sectionData && (
        <GeoJson
          {...mapProps}
          data={sectionData}
          hover={false}
          styleCallback={(feature: { properties?: { id?: string } }) => {
            const id = feature?.properties?.id;
            const active = !activeSectionId || activeSectionId === id;
            return {
              strokeWidth: active ? "2.5" : "1.5",
              stroke: active ? "#0B3D91" : "rgba(11,61,145,0.35)",
              strokeDasharray: "6 5",
              fill: "none",
              r: "0",
            };
          }}
        />
      )}
      {sections.map((s) => {
        const focused = !activeSectionId || activeSectionId === s.id;
        const dim = Boolean(activeSectionId && activeSectionId !== s.id);
        const opacity = dim ? 0.35 : 1;
        const labelThis = showLabels && focused;
        return (
          <React.Fragment key={s.id}>
            <Marker
              {...mapProps}
              anchor={[s.start.lat, s.start.lng]}
              width={dim ? 20 : 28}
              color="#2E7D32"
              style={{ opacity, zIndex: 4, pointerEvents: "auto" }}
            />
            {labelThis && (
              <Overlay
                {...mapProps}
                anchor={[s.start.lat, s.start.lng]}
                offset={[12, -8]}
              >
                <div className="raft-section-pin raft-section-pin--start">
                  Start · {s.start.name}
                </div>
              </Overlay>
            )}
            <Marker
              {...mapProps}
              anchor={[s.end.lat, s.end.lng]}
              width={dim ? 20 : 28}
              color="#C62828"
              style={{ opacity, zIndex: 4, pointerEvents: "auto" }}
            />
            {labelThis && (
              <Overlay
                {...mapProps}
                anchor={[s.end.lat, s.end.lng]}
                offset={[12, -8]}
              >
                <div className="raft-section-pin raft-section-pin--end">
                  End · {s.end.name}
                </div>
              </Overlay>
            )}
          </React.Fragment>
        );
      })}
    </>
  );
};

export default HighlightRiverOverlay;
